import React, { useState, useEffect } from 'react';
import RelativeTime from './RelativeTime';
import { useClient } from '../connect_api';
import { create } from '@bufbuild/protobuf';
import { 
    CustomerService,
    GetCustomerSMSDeliveryStatusRequestSchema,
    type SMSDeliveryStatus
} from '../api/api_pb';

interface SMSDeliveryLogsProps {
    customerId: string;
}

const SMSDeliveryLogs: React.FC<SMSDeliveryLogsProps> = ({ customerId }) => {
    const client = useClient(CustomerService);
    const [smsLogs, setSmsLogs] = useState<SMSDeliveryStatus[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchSMSLogs = async () => {
            setIsLoading(true);
            try {
                const smsStatusRequest = create(GetCustomerSMSDeliveryStatusRequestSchema, {
                    customerUuid: customerId,
                    limit: 20
                });

                const smsStatusResponse = await (client as any).getCustomerSMSDeliveryStatus(smsStatusRequest);
                setSmsLogs(smsStatusResponse.smsStatuses);
            } catch (error) {
                console.error('Failed to fetch SMS delivery logs:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSMSLogs();
    }, [customerId, client]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'delivered':
                return (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Delivered
                    </span>
                );
            case 'failed':
                return (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        Failed
                    </span>
                );
            case 'pending':
                return (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Pending
                    </span>
                );
            default:
                return (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {status}
                    </span>
                );
        }
    };

    const getMessageTypeBadge = (messageType: string) => {
        switch (messageType) {
            case 'login_pin':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded">
                        Login PIN
                    </span>
                );
            case 'pin_reset':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-orange-50 text-orange-700 rounded">
                        PIN Reset
                    </span>
                );
            case 'notification':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-purple-50 text-purple-700 rounded">
                        Notification
                    </span>
                );
            default:
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-gray-50 text-gray-700 rounded">
                        {messageType}
                    </span>
                );
        }
    };

    if (isLoading) {
        return <div className="text-center py-4">Loading SMS delivery logs...</div>;
    }

    if (smsLogs.length === 0) {
        return <div className="text-center py-4 text-gray-500">No SMS delivery logs found</div>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 p-2 text-left">Sent At</th>
                        <th className="border border-gray-300 p-2 text-left">Message Type</th>
                        <th className="border border-gray-300 p-2 text-left">Status</th>
                        <th className="border border-gray-300 p-2 text-left">Delivered At</th>
                        <th className="border border-gray-300 p-2 text-left">Failure Reason</th>
                    </tr>
                </thead>
                <tbody>
                    {smsLogs.map((sms) => {
                        return (
                            <tr key={sms.uuid} className="hover:bg-gray-50">
                                <td className="border border-gray-300 p-2">
                                    {sms.sentAt ? (
                                        <div className="text-sm">
                                            <RelativeTime timestamp={sms.sentAt} />
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">N/A</span>
                                    )}
                                </td>
                                <td className="border border-gray-300 p-2">
                                    {getMessageTypeBadge(sms.messageType)}
                                </td>
                                <td className="border border-gray-300 p-2">
                                    {getStatusBadge(sms.status)}
                                </td>
                                <td className="border border-gray-300 p-2">
                                    {sms.deliveredAt ? (
                                        <div className="text-sm">
                                            <RelativeTime timestamp={sms.deliveredAt} />
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="border border-gray-300 p-2 text-sm">
                                    {sms.failureReason || '-'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default SMSDeliveryLogs;